import { useState } from "react";
import Button from "@/components/ui/button";
import VideoRecorder from "../video-recorder";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type { TestimonialAnswerType } from "../../types";
import Textarea from "@/components/ui/textarea";
import IconLightbulb from "@/components/icons/icon-lightbulb";
import StepLayout from "../step-layout";

export default function StepTestimonialQ1() {
	const { data, setTestimonialAnswer, nextStep, canProceed } = useFeedbackFlow();
	const [selectedType, setSelectedType] = useState<TestimonialAnswerType | null>(
		data.testimonialData.question1.textContent || data.testimonialData.question1.videoFile ? data.testimonialData.question1.type : null
	);

	const handleSelectType = (type: TestimonialAnswerType) => {
		setSelectedType(type);
		setTestimonialAnswer("question1", { type });
	};

	const handleTextChange = (text: string) => {
		setTestimonialAnswer("question1", { textContent: text });
	};

	const handleVideoComplete = (file: File, previewUrl: string) => {
		setTestimonialAnswer("question1", {
			videoFile: file,
			videoPreview: previewUrl,
		});
	};

	const handleVideoReset = () => {
		setTestimonialAnswer("question1", {
			videoFile: null,
			videoPreview: null,
		});
	};

	const handleBack = () => {
		setSelectedType(null);
	};

	return (
		<StepLayout
			header={
				<div className="mb-4 shrink-0">
					<h1 className="text-2xl font-semibold text-primary mb-2">Introduce yourself and share why you love using our product</h1>
					<p className="text-secondary leading-relaxed">In a few sentences, share what you love about using our product.</p>
				</div>
			}
			content={
				<div className="flex-1 min-h-0 flex flex-col">
					{/* Tips */}
					<div className="mb-8 shrink-0">
						<div className="flex items-center gap-2 mb-2">
							<IconLightbulb className="w-5 h-5 text-amber-400" />
							<p className="font-medium text-primary">Tips:</p>
						</div>
						<ul className="text-secondary space-y-1">
							<li className="flex items-start gap-2">
								<span className="text-gray-400">•</span>
								<span>Share where you found us, ex. "I found you on Product Hunt"</span>
							</li>
							<li className="flex items-start gap-2">
								<span className="text-gray-400">•</span>
								<span>Share any results you've had since you signed up, ex. "My conversion rate increased by 30%"</span>
							</li>
						</ul>
					</div>

					{/* Input area - switches between choice cards and input */}
					<div className="flex-1 min-h-0 flex flex-col">
						{selectedType === null ? (
							<div className="space-y-3">
								<p className="font-medium text-primary">How would you like to share your testimonial?</p>

								{/* Record Video Option */}
								<button
									type="button"
									onClick={() => handleSelectType("video")}
									className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-brand hover:bg-brand/5 transition-all text-left group cursor-pointer"
								>
									<div className="flex items-center gap-4">
										<div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
											<svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
												/>
											</svg>
										</div>
										<div>
											<p className="font-medium text-primary">Record a video</p>
											<p className="text-sm text-secondary">Share your story on camera</p>
										</div>
									</div>
								</button>

								{/* Write Text Option */}
								<button
									type="button"
									onClick={() => handleSelectType("text")}
									className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-brand hover:bg-brand/5 transition-all text-left group cursor-pointer"
								>
									<div className="flex items-center gap-4">
										<div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
											<svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
												/>
											</svg>
										</div>
										<div>
											<p className="font-medium text-primary">Write it as text</p>
											<p className="text-sm text-secondary">Type out your testimonial</p>
										</div>
									</div>
								</button>
							</div>
						) : (
							<div className="flex flex-col flex-1 min-h-0">
								<button type="button" onClick={handleBack} className="text-sm text-secondary hover:text-primary mb-3 flex items-center gap-1 shrink-0">
									<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
									</svg>
									Change format
								</button>
								<div className="flex-1 min-h-0">
									{selectedType === "text" ? (
										<Textarea
											sizeVariant="large"
											value={data.testimonialData.question1.textContent}
											onChange={(e) => handleTextChange(e.target.value)}
											placeholder="Write your testimonial here..."
											className="h-32"
										/>
									) : (
										<VideoRecorder onRecordingComplete={handleVideoComplete} existingVideoFile={data.testimonialData.question1.videoFile} onReset={handleVideoReset} fullHeight />
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			}
			footer={
				<div className="shrink-0 pt-6">
					<Button onClick={nextStep} variant="brand" className="w-full" disabled={!canProceed()}>
						Continue
					</Button>
				</div>
			}
		/>
	);
}
